import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Check, X, ClipboardText, MagnifyingGlass, Warning, Trash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';
import DeleteUserModal from '../components/DeleteUserModal';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGIONS = ['North', 'South', 'East', 'West'];

const CLASS_COLOR = {
  // New 5-level
  'Level 0 – Process Failure (No Personal Memo)': '#10B981',
  'Level 1 – Honest Mistake / Slip': '#3B82F6',
  'Level 2 – At-Risk Behaviour': '#F59E0B',
  'Level 3 – Recklessness / Negligence': '#EF4444',
  'Level 4 – Wilful Violation / Misconduct': '#991B1B',
  // Legacy 4-level
  'Level 1 – Process Failure': '#10B981',
  'Level 2 – Human Error': '#3B82F6',
  'Level 3 – Negligence': '#EF4444',
  'Level 4 – Willful Violation': '#991B1B',
};

const UserManagement = () => {
  const { user } = useAuthStore();
  const [tab, setTab] = useState('employees'); // 'employees' | 'pending'
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveUser, setApproveUser] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchPending = useCallback(async () => {
    try { const r = await axios.get(`${API}/auth/pending-users`); setPendingUsers(r.data || []); }
    catch (err) { console.error(err); }
  }, []);

  const fetchAll = useCallback(async () => {
    try { const r = await axios.get(`${API}/auth/all-users-with-memo-stats`); setAllUsers(r.data.users || []); }
    catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    Promise.allSettled([fetchPending(), fetchAll()]).finally(() => setLoading(false));
  }, [fetchPending, fetchAll]);

  const handleApprove = async () => {
    if (!approveUser) return;
    const needsRegion = ['Sales Executive', 'Service Executive', 'Sales Coordinator', 'Tender Executive', 'Regional Sales Manager'].includes(approveUser.role);
    if (needsRegion && !selectedRegion) { notify('Please assign a region'); return; }
    try {
      await axios.post(`${API}/auth/approve-user/${approveUser.id}`, null, { params: { region: selectedRegion || '' } });
      setApproveUser(null);
      fetchPending(); fetchAll();
    } catch (err) { notify(err.response?.data?.detail || 'Error'); }
  };

  const handleReject = async (userId) => {
    const ok = await confirm({
      title: 'Reject user?',
      message: 'This user’s signup request will be permanently rejected.',
      confirmText: 'Reject',
      danger: true,
    });
    if (!ok) return;
    try { await axios.post(`${API}/auth/reject-user/${userId}`); fetchPending(); }
    catch (err) { notify(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteUser = async (reason) => {
    if (!deleteTarget) return;
    await axios.delete(`${API}/team/members/${deleteTarget.id}`, { params: { reason } });
    setDeleteTarget(null);
    fetchAll();
  };

  if (user?.role !== 'President') {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="medical-card p-12 text-center">
          <h2 className="text-2xl font-bold text-[#052A23] mb-2">Access Denied</h2>
          <p className="text-[#6D8E87]">Only Presidents can manage users</p>
        </div>
      </div>
    );
  }

  if (loading) return <PageSkeleton />;

  const filteredAll = allUsers.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s) ||
      (u.role || '').toLowerCase().includes(s) || (u.employee_id || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="user-management-page">
      <div className="mb-6">
        <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="page-title">User Management</h1>
        <p className="text-[#6D8E87]">Manage approvals and view employee accountability history</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-white border border-[#D5E8E4] rounded-full inline-flex w-fit mb-5">
        <button onClick={() => setTab('employees')}
          className={`px-5 py-1.5 rounded-full text-sm font-medium transition ${tab === 'employees' ? 'bg-[#00a69c] text-white' : 'text-[#3B5A54]'}`}
          data-testid="tab-employees">
          All Employees ({allUsers.length})
        </button>
        <button onClick={() => setTab('pending')}
          className={`px-5 py-1.5 rounded-full text-sm font-medium transition flex items-center gap-2 ${tab === 'pending' ? 'bg-[#00a69c] text-white' : 'text-[#3B5A54]'}`}
          data-testid="tab-pending">
          Pending Approvals
          {pendingUsers.length > 0 && <span className="px-2 py-0.5 bg-red-500 text-white rounded-full text-[10px] font-bold">{pendingUsers.length}</span>}
        </button>
      </div>

      {tab === 'employees' && (
        <>
          <div className="relative mb-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, role..."
              className="pl-9 pr-3 py-2 text-sm border border-[#D5E8E4] rounded-lg w-full sm:w-80 bg-white" data-testid="emp-search" />
          </div>

          {filteredAll.length === 0 ? (
            <div className="medical-card p-12 text-center"><p className="text-[#6D8E87]">No employees match.</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="employees-grid">
              {filteredAll.map(u => {
                const s = u.memo_stats || {};
                const total = s.total || 0;
                const concern = total >= 3 || (s.by_classification?.['Level 3 – Recklessness / Negligence'] || 0) > 0 || (s.by_classification?.['Level 4 – Wilful Violation / Misconduct'] || 0) > 0 || (s.by_classification?.['Level 3 – Negligence'] || 0) > 0 || (s.by_classification?.['Level 4 – Willful Violation'] || 0) > 0;
                return (
                  <div key={u.id} className="medical-card p-5 hover:shadow-md transition" data-testid={`employee-card-${u.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-full bg-[#E2F3F0] text-[#00a69c] font-semibold flex items-center justify-center shrink-0">
                          {(u.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[#052A23] truncate" style={{ fontFamily: 'Outfit' }}>{u.name}</div>
                          <div className="text-xs text-[#6D8E87] truncate">{u.role}{u.region ? ` · ${u.region}` : ''}</div>
                        </div>
                      </div>
                      {concern && (
                        <span title="Multiple memos or severe classification" className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700 rounded-full" data-testid={`concern-${u.id}`}>
                          <Warning size={10} weight="bold" /> Review
                        </span>
                      )}
                      {u.id !== user?.id && (
                        <button
                          onClick={() => setDeleteTarget(u)}
                          className="ml-2 p-1.5 rounded-md text-rose-600 hover:bg-rose-50 transition shrink-0"
                          title={`Remove ${u.name}`}
                          data-testid={`delete-user-btn-${u.id}`}
                        >
                          <Trash size={16} weight="bold" />
                        </button>
                      )}
                    </div>

                    <div className="text-xs text-[#6D8E87] space-y-0.5 mb-3">
                      <div className="truncate">📧 {u.email}</div>
                      {u.employee_id && <div>🆔 {u.employee_id}</div>}
                    </div>

                    {/* Memo summary */}
                    <div className="pt-3 border-t border-[#F4F9F7]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#3B5A54]">
                          <ClipboardText size={14} className="text-[#00a69c]" weight="bold" />
                          Memo History
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${total === 0 ? 'bg-emerald-100 text-emerald-700' : total >= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}
                          data-testid={`memo-count-${u.id}`}>
                          {total} {total === 1 ? 'memo' : 'memos'}
                        </span>
                      </div>
                      {total === 0 ? (
                        <p className="text-xs text-[#6D8E87] italic">Clean record — no memos on file.</p>
                      ) : (
                        <>
                          <div className="grid grid-cols-3 gap-1.5 text-[10px] text-center mb-2">
                            <div className="px-1.5 py-1 bg-emerald-50 rounded">
                              <div className="font-bold text-emerald-700">{s.acknowledged || 0}</div>
                              <div className="text-emerald-700/70 uppercase tracking-wide">Ack</div>
                            </div>
                            <div className="px-1.5 py-1 bg-amber-50 rounded">
                              <div className="font-bold text-amber-700">{s.pending || 0}</div>
                              <div className="text-amber-700/70 uppercase tracking-wide">Pending</div>
                            </div>
                            <div className="px-1.5 py-1 bg-red-50 rounded">
                              <div className="font-bold text-red-700">{s.disputed || 0}</div>
                              <div className="text-red-700/70 uppercase tracking-wide">Disputed</div>
                            </div>
                          </div>
                          {/* Classification chips */}
                          {Object.keys(s.by_classification || {}).length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {Object.entries(s.by_classification).map(([cls, n]) => (
                                <span key={cls} className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: `${CLASS_COLOR[cls] || '#6D8E87'}20`, color: CLASS_COLOR[cls] || '#6D8E87' }}>
                                  {cls.split(' – ')[0]} × {n}
                                </span>
                              ))}
                            </div>
                          )}
                          {(s.penalty_total || 0) > 0 && (
                            <div className="text-xs text-[#3B5A54]">
                              <span className="text-[#6D8E87]">Penalty total:</span> <span className="font-semibold text-[#052A23]">₹{s.penalty_total.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                          <a href={`/memos`} className="block text-xs text-[#00a69c] font-medium hover:underline mt-2">View all memos →</a>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'pending' && (
        pendingUsers.length === 0 ? (
          <div className="medical-card p-12 text-center" data-testid="no-pending-users">
            <div className="w-16 h-16 bg-[#E2F3F0] rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-[#00a69c]" weight="bold" />
            </div>
            <h3 className="text-xl font-semibold text-[#052A23] mb-2">All Caught Up!</h3>
            <p className="text-[#6D8E87]">No pending user registrations</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="pending-users-list">
            {pendingUsers.map((pu) => (
              <div key={pu.id} className="medical-card p-6" data-testid={`user-${pu.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <h3 className="text-xl font-semibold text-[#052A23] mb-3" style={{ fontFamily: 'Outfit' }}>{pu.name}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm text-[#3B5A54]">
                      <div><span className="font-medium text-[#6D8E87]">Email:</span><p>{pu.email}</p></div>
                      <div><span className="font-medium text-[#6D8E87]">Employee ID:</span><p>{pu.employee_id}</p></div>
                      <div><span className="font-medium text-[#6D8E87]">Aadhar:</span><p>{pu.aadhar_number}</p></div>
                      <div><span className="font-medium text-[#6D8E87]">DOJ:</span><p>{new Date(pu.date_of_joining).toLocaleDateString()}</p></div>
                      <div><span className="font-medium text-[#6D8E87]">Role:</span><p className="font-medium text-[#00a69c]">{pu.role}</p></div>
                      <div><span className="font-medium text-[#6D8E87]">Registered:</span><p>{new Date(pu.created_at).toLocaleDateString()}</p></div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => handleReject(pu.id)} className="flex items-center gap-2 px-4 py-2 bg-red-50 text-[#EF4444] rounded-lg hover:bg-red-100 transition" data-testid={`reject-${pu.id}`}>
                      <X size={20} weight="bold" /> Reject
                    </button>
                    <button onClick={() => { setApproveUser(pu); setSelectedRegion(''); }} className="flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg hover:bg-[#00bfb3] transition" data-testid={`approve-${pu.id}`}>
                      <Check size={20} weight="bold" /> Approve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Approve Modal */}
      {approveUser && (
        <Dialog open={!!approveUser} onOpenChange={() => setApproveUser(null)}>
          <DialogContent className="max-w-md" data-testid="approve-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Approve {approveUser.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-3">
              <div className="p-3 bg-[#F4F9F7] rounded-lg text-sm">
                <p><span className="text-[#6D8E87]">Role:</span> <strong>{approveUser.role}</strong></p>
                <p><span className="text-[#6D8E87]">Email:</span> {approveUser.email}</p>
              </div>
              {['Sales Executive', 'Service Executive', 'Sales Coordinator', 'Tender Executive', 'Regional Sales Manager'].includes(approveUser.role) && (
                <div>
                  <label className="block text-sm font-medium text-[#3B5A54] mb-2">Assign Region *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {REGIONS.map(r => (
                      <button key={r} onClick={() => setSelectedRegion(r)}
                        className={`py-2.5 rounded-lg border-2 text-sm font-medium transition ${selectedRegion === r ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}
                        data-testid={`region-${r.toLowerCase()}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setApproveUser(null)} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleApprove} className="flex-1 py-3 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3]" data-testid="confirm-approve-btn">
                  Confirm Approval
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <DeleteUserModal
        open={!!deleteTarget}
        target={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteUser}
      />
    </div>
  );
};

export default UserManagement;
